import * as ngCore from '@angular/core'
import { ComponentStore } from '@ngrx/component-store'

import * as models from './models'

export interface State {
  type: 'range' | 'multirange'
  selectedDateRange?: models.DateRange
  editingDateRange?: models.DateRange
  // Tracks which edge of the date range was modified last ('start' or 'end').
  // This helps determine which date to anchor when changing a duration:
  // - If 'start' was last changed, we calculate 'end' based on the changed duration.
  // - If 'end' was last changed, we calculate 'start' instead.
  lastChanged?: 'start' | 'end'
}

export namespace State {
  export const INITIAL: NoMissing<State> = {
    editingDateRange: undefined,
    lastChanged: undefined,
    selectedDateRange: undefined,
    type: 'range',
  }
}

@ngCore.Injectable()
export class Store extends ComponentStore<State> {
  /** @see {@link State.selectedDateRange} */
  public readonly selectedDateRangeSg = this.selectSignal(
    state => state.selectedDateRange ?? models.DateRange.generate(),
  )

  /** @see {@link State.type} */
  public readonly typeSg = this.selectSignal(state => state.type)

  /** @see {@link State.editingDateRange} */
  public readonly editingDateRangeSg = this.selectSignal(
    state => state.editingDateRange ?? models.DateRange.generate(),
  )

  /** @see {@link State.lastChanged} */
  public readonly lastChangedSg = this.selectSignal(state => state.lastChanged)

  constructor() {
    super(State.INITIAL)
  }

  /**
   * Start editing the currently selected date range by copying it
   * into the `editingDateRange` state. This enables editing in an isolated state
   * without immediately affecting the selected range.
   */
  public startEditing() {
    const selectedDateRange = this.selectedDateRangeSg()

    if (selectedDateRange) {
      this.patchState({ editingDateRange: selectedDateRange })
    }
  }

  /**
   * Finalize editing by applying the changes from `editingDateRange`
   * to `selectedDateRange`, and then clearing the editing state.
   */
  public finishEditing() {
    const editingDateRange = this.editingDateRangeSg()

    this.patchState({
      editingDateRange: undefined,
      selectedDateRange: editingDateRange,
    })
  }

  /**
   * Discard any in-progress edits by clearing the `editingDateRange` state
   * without applying the changes to `selectedDateRange`.
   */
  public discardEditing() {
    this.patchState({
      editingDateRange: undefined,
    })
  }
}
